<script setup lang="ts">
import { useUnifiedSearchProvider } from "~/composables/useUnifiedSearchProvider";
import { provideSearchProvider } from "~/composables/searchProvider";

// Pages can opt out of the global chrome (header + chat widget) by setting
// `definePageMeta({ hideChrome: true })`. Used by the login page so the
// `<e-layout-login>` shell can claim the full viewport without a sticky
// header overlapping the centered card.
// NuxtRouteAnnouncer is ClientOnly: its SSR output (an empty <div>) does not
// match its client render (<span class="nuxt-route-announcer">), which made
// EVERY page hydration-mismatch at the app root — Vue re-patched the whole
// root sibling list (header, page, chat) on each load. The announcer only
// speaks on client-side navigations anyway, so nothing is lost by mounting
// it client-only.
const route = useRoute();
const hideChrome = computed(() => route.meta.hideChrome === true);

// The docs layout uses <e-layout-docs>, which owns the full viewport and renders
// its own mega-menu in its `header` slot. Suppress the global header there so the
// header isn't doubled and the layout's single-scroll-area model isn't broken by
// a sticky header above it. Chat widget still shows on docs.
const hideGlobalHeader = computed(
  () =>
    hideChrome.value ||
    route.meta.hideHeader === true ||
    route.meta.layout === "docs" ||
    // The /start flow owns its own chrome via a dedicated chromeless layout.
    route.meta.layout === "start-page",
);

// Hide the floating chat while the CMS editor panel is open — it would overlap
// the editor's action buttons. (When collapsed, the page panel is full width so
// there's nothing to overlap.)
const cms = useCmsEditor();
// /start flows get Cora only when their contract opts in (`meta.chat`, published
// by the start page); everywhere else she shows unless hidden above.
const startChat = useState("start:chat", () => false);
const showChat = computed(
  () =>
    !hideChrome.value &&
    !cms.open.value &&
    (route.meta.layout !== "start-page" || startChat.value),
);
// Signal to DOM-mutating entry animations (hero SplitText) that they should stay
// static while editing — live re-patching over SplitText DOM corrupts it.
provide("cms:previewing", cms.previewing);

// Site-wide search lives here (above the layout swap) so it's available on
// every page and searches both zones (commercial + docs) at once, weighted by
// where you are. This is the default provider; the api-reference layout swaps
// in its in-page operation search while mounted and restores the previous
// provider on unmount (see api-reference.vue), so there's no route-string
// coupling here.
const unifiedSearch = useUnifiedSearchProvider(
  computed(() => localeFromUrl(route.path)),
);
provideSearchProvider(unifiedSearch);
</script>

<template>
  <ClientOnly>
    <NuxtRouteAnnouncer />
  </ClientOnly>
  <NuxtLoadingIndicator color="var(--color-accent)" :height="3" :duration="2000" :throttle="500" />
  <TheHeader v-if="!hideGlobalHeader" />
  <CmsShell>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <LazyIdleChatWidget v-if="showChat" hydrate-on-idle />
  </CmsShell>
</template>

<style>
/* "page-fade" transition, applied via definePageMeta on commercial routes
   (index.vue + [...slug].vue). Leave is quick so the swap feels responsive;
   enter is slightly longer to cover the hero entry setup. */
.page-fade-enter-active {
  transition: opacity 0.25s ease-out;
}
.page-fade-leave-active {
  transition: opacity 0.15s ease-in;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
